import { useCallback, useEffect, useMemo } from 'react';
import _ from 'lodash';

import usePlansStore, { EditablePlan } from '@/modules/Renewals/RenewalSetup/stores/usePlans.store';

const EMPTY_FIELD_ERROR_MESSAGE = "Can't be empty";
export const UNIQUE_PLAN_CODE_ERROR = 'Must be unique';

export const PlanField = {
  PlanCode: 'planCode',
  PlanName: 'planName',
} as const;

export type PlanFieldType = (typeof PlanField)[keyof typeof PlanField];

type DefaultFieldKey = 'defaultPlanCode' | 'defaultPlanName';
const DEFAULT_KEY_BY_FIELD: Record<PlanFieldType, DefaultFieldKey> = {
  planCode: 'defaultPlanCode',
  planName: 'defaultPlanName',
} as const;

export const validatePlans = (plans: EditablePlan[], serverInvalidCodes: string[] = []) => {
  const errors: Record<string, Partial<Record<PlanFieldType, string[]>>> = {};

  plans.forEach((plan) => {
    [PlanField.PlanCode, PlanField.PlanName].forEach((field) => {
      if (!plan[field] || plan[field].trim().length === 0) {
        errors[plan.id] = {
          ...(errors[plan.id] || {}),
          [field]: [EMPTY_FIELD_ERROR_MESSAGE],
        };
      }
    });
  });

  const codeCount: Record<string, string[]> = {};
  plans.forEach((plan) => {
    const code = plan.planCode.trim();
    if (!code) return;
    if (!codeCount[code]) codeCount[code] = [];
    codeCount[code].push(plan.id);
  });
  const duplicates = Object.values(codeCount)
    .filter((ids) => ids.length > 1)
    .flat();

  const serverInvalidIds = plans
    .filter((plan) => serverInvalidCodes.includes(plan.planCode))
    .map((plan) => plan.id);

  [...duplicates, ...serverInvalidIds].forEach((id) => {
    errors[id] = {
      ...(errors[id] || {}),
      planCode: [
        ...(errors[id]?.planCode?.filter((msg) => msg !== UNIQUE_PLAN_CODE_ERROR) || []),
        UNIQUE_PLAN_CODE_ERROR,
      ],
    };
  });

  return errors;
};

export const usePlanFieldError = (planId?: string, field?: PlanFieldType) =>
  usePlansStore(
    useCallback(
      (service) => (planId && field ? service.state.errors?.[planId]?.[field]?.[0] : undefined),
      [planId, field]
    )
  );

export const usePlansActions = () => {
  const setState = usePlansStore((state) => state.setState);

  const updateEntity = useCallback(
    (id: string, field: PlanFieldType, value: string) => {
      setState((prev) => {
        const plans = (prev.plans || []).map((plan) => (plan.id === id ? { ...plan, [field]: value } : plan));

        return { plans, errors: prev.errors };
      });
    },
    [setState]
  );

  const updateEntityDebounced = useMemo(
    () =>
      _.debounce((id: string, field: PlanFieldType, value: string) => {
        updateEntity(id, field, value);
      }, 250),
    [updateEntity]
  );

  const resetEntity = (id: string, field: PlanFieldType) => {
    setState((prev) => {
      const plans = (prev.plans || []).map((plan) =>
        plan.id === id ? { ...plan, [field]: plan[DEFAULT_KEY_BY_FIELD[field]] } : plan
      );

      return { plans, errors: prev.errors };
    });
  };

  const setFieldErrors = (id: string, field: PlanFieldType, messages: string | string[]) => {
    const list = Array.isArray(messages) ? messages : [messages];
    setState((prev) => ({
      plans: prev.plans,
      errors: {
        ...(prev.errors || {}),
        [id]: { ...(prev.errors?.[id] || {}), [field]: list },
      },
    }));
  };

  useEffect(() => () => updateEntityDebounced.cancel(), [updateEntityDebounced]);

  return { updateEntity, updateEntityDebounced, resetEntity, setFieldErrors };
};

export const useEditablePlan = (planId?: string) =>
  usePlansStore(
    useCallback(
      (state) =>
        planId ? (state.state.plans || []).find((plan: EditablePlan) => plan.id === planId) : undefined,
      [planId]
    )
  );
